import { useSelect } from "@refinedev/antd";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useNotification,
} from "@refinedev/core";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { IShipco, IVessel } from "interfaces";
import React from "react";
import { RecipientsInput } from "./components/RecipientsInput";
import { AlertConfigRow, AlertStateRow, Paginated } from "./lib/types";

const PAGE_LIMIT = 25;

// Fired badges colour the ALERT thresholds (75/90/100), which deliberately
// differ from the consumption bands (cards moved to 50/75/100, ruling
// 2026-08-21; alert emails stayed put). Keeping a dedicated map preserves
// the gold/orange/red distinction between a fired 75 and a fired 90.
const ALERT_THRESHOLD_COLORS: Record<number, string> = {
  75: "gold",
  90: "orange",
  100: "red",
};

type ScopeType = "vessel" | "shipco";

interface DraftEdit {
  enabled: boolean;
  recipients: string[];
}

const scopeKey = (scopeType: string, scopeId: number) =>
  `${scopeType}:${scopeId}`;

// ---------------------------------------------------------------------------
// Per-tab config table: search + server pagination + per-row edit/save.
// ---------------------------------------------------------------------------

interface AlertConfigPanelProps {
  scopeType: ScopeType;
  firedByScope: Map<string, AlertStateRow[]>;
  reloadToken: number;
}

const AlertConfigPanel: React.FC<AlertConfigPanelProps> = ({
  scopeType,
  firedByScope,
  reloadToken,
}) => {
  const apiUrl = useApiUrl();
  const { open: notify } = useNotification();
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [drafts, setDrafts] = React.useState<Map<string, DraftEdit>>(new Map());
  const [savingKey, setSavingKey] = React.useState<string | null>(null);

  const {
    result: configResult,
    query: { isLoading, refetch },
  } = useCustom<Paginated<AlertConfigRow>>({
    url: `${apiUrl}/flat-fee-alerts/config`,
    method: "get",
    config: {
      query: {
        scopeType,
        search: search || undefined,
        page,
        limit: PAGE_LIMIT,
        // Not read server-side - only here to bust the query cache when a
        // "New alert" is created elsewhere on the page for this scope type.
        _reload: reloadToken,
      },
    },
  });

  const { mutate } = useCustomMutation();

  const configs = configResult?.data;
  const rows = configs?.data ?? [];

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const getDraft = (row: AlertConfigRow): DraftEdit =>
    drafts.get(scopeKey(row.scopeType, row.scopeId)) ?? {
      enabled: row.enabled,
      recipients: row.recipients,
    };

  const updateDraft = (row: AlertConfigRow, patch: Partial<DraftEdit>) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      const key = scopeKey(row.scopeType, row.scopeId);
      next.set(key, { ...getDraft(row), ...patch });
      return next;
    });
  };

  const isDirty = (row: AlertConfigRow) => {
    const draft = drafts.get(scopeKey(row.scopeType, row.scopeId));
    if (!draft) return false;
    return (
      draft.enabled !== row.enabled ||
      draft.recipients.length !== row.recipients.length ||
      draft.recipients.some((r, i) => r !== row.recipients[i])
    );
  };

  const handleSave = (row: AlertConfigRow) => {
    const draft = getDraft(row);

    // Client-side guard mirroring the API rule - avoids a round trip for the
    // most common invalid state.
    if (draft.enabled && draft.recipients.length === 0) {
      notify?.({
        type: "error",
        message: "Cannot save",
        description: "recipients required when enabled",
      });
      return;
    }

    const key = scopeKey(row.scopeType, row.scopeId);
    setSavingKey(key);
    mutate(
      {
        url: `${apiUrl}/flat-fee-alerts/config`,
        method: "put",
        values: {
          scopeType: row.scopeType,
          scopeId: row.scopeId,
          enabled: draft.enabled,
          recipients: draft.recipients,
        },
      },
      {
        onError: (error: any) => {
          const message = error?.response?.data?.message;
          notify?.({
            type: "error",
            message: "Save Failed",
            description:
              (Array.isArray(message) ? message.join(", ") : message) ||
              error?.message ||
              "An error occurred while saving the alert.",
          });
        },
        onSuccess: () => {
          notify?.({
            type: "success",
            message: "Saved",
            description: `Alert settings saved for ${row.scopeName ?? `#${row.scopeId}`}.`,
          });
          setDrafts((prev) => {
            const next = new Map(prev);
            next.delete(key);
            return next;
          });
          refetch();
        },
        onSettled: () => setSavingKey(null),
      }
    );
  };

  const columns: ColumnsType<AlertConfigRow> = [
    {
      title: "Name",
      dataIndex: "scopeName",
      key: "scopeName",
      render: (_, row) => row.scopeName ?? `#${row.scopeId}`,
    },
    {
      title: "Enabled",
      key: "enabled",
      width: 90,
      render: (_, row) => {
        const draft = getDraft(row);
        return (
          <Switch
            checked={draft.enabled}
            onChange={(checked) => updateDraft(row, { enabled: checked })}
          />
        );
      },
    },
    {
      title: "Recipients",
      key: "recipients",
      render: (_, row) => {
        const draft = getDraft(row);
        return (
          <RecipientsInput
            value={draft.recipients}
            onChange={(recipients) => updateDraft(row, { recipients })}
          />
        );
      },
    },
    {
      title: "Fired",
      key: "fired",
      render: (_, row) => {
        const fired = firedByScope.get(scopeKey(row.scopeType, row.scopeId)) ?? [];
        return (
          <>
            {fired.map((state) => (
              <Tag
                key={state.id}
                color={ALERT_THRESHOLD_COLORS[state.threshold] ?? "default"}
              >
                {state.threshold}%
              </Tag>
            ))}
          </>
        );
      },
    },
    {
      title: "",
      key: "save",
      width: 100,
      render: (_, row) => {
        const key = scopeKey(row.scopeType, row.scopeId);
        return (
          <Button
            size="small"
            type="primary"
            disabled={!isDirty(row) || savingKey !== null}
            loading={savingKey === key}
            onClick={() => handleSave(row)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Input.Search
        allowClear
        placeholder={`Search ${scopeType === "vessel" ? "vessel" : "shipco"}`}
        style={{ maxWidth: 320, marginBottom: 16 }}
        onSearch={handleSearch}
        onChange={(e) => {
          if (!e.target.value) handleSearch("");
        }}
      />
      <Table<AlertConfigRow>
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: configs?.page ?? page,
          pageSize: PAGE_LIMIT,
          total: configs?.total ?? 0,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// "New alert" modal: scopeType fixed to whichever tab is active, scope picked
// from the existing vessel/shipco CRUD resources, submitted through the same
// upserting PUT the row-level Save uses.
// ---------------------------------------------------------------------------

interface NewAlertModalProps {
  open: boolean;
  scopeType: ScopeType;
  onClose: () => void;
  onCreated: (scopeType: ScopeType) => void;
}

interface NewAlertFormValues {
  scopeId: string;
  enabled: boolean;
  recipients: string[];
}

const NewAlertModal: React.FC<NewAlertModalProps> = ({
  open,
  scopeType,
  onClose,
  onCreated,
}) => {
  const apiUrl = useApiUrl();
  const { open: notify } = useNotification();
  const [form] = Form.useForm<NewAlertFormValues>();

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "id",
    pagination: { mode: "server" },
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "id",
    pagination: { mode: "server" },
  });

  const scopeSelectProps =
    scopeType === "vessel" ? vesselSelectProps : shipcoSelectProps;

  const {
    mutate,
    mutation: { isPending },
  } = useCustomMutation();

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        if (values.enabled && (values.recipients ?? []).length === 0) {
          notify?.({
            type: "error",
            message: "Cannot create alert",
            description: "recipients required when enabled",
          });
          return;
        }

        mutate(
          {
            url: `${apiUrl}/flat-fee-alerts/config`,
            method: "put",
            values: {
              scopeType,
              scopeId: Number(values.scopeId),
              enabled: values.enabled,
              recipients: values.recipients,
            },
          },
          {
            onError: (error: any) => {
              const message = error?.response?.data?.message;
              notify?.({
                type: "error",
                message: "Save Failed",
                description:
                  (Array.isArray(message) ? message.join(", ") : message) ||
                  error?.message ||
                  "An error occurred while creating the alert.",
              });
            },
            onSuccess: () => {
              notify?.({
                type: "success",
                message: "Alert Created",
                description: "Alert settings saved.",
              });
              form.resetFields();
              onCreated(scopeType);
              onClose();
            },
          }
        );
      })
      .catch(() => undefined);
  };

  return (
    <Modal
      open={open}
      title="New alert"
      okText="Create"
      confirmLoading={isPending}
      cancelButtonProps={{ disabled: isPending }}
      closable={!isPending}
      maskClosable={false}
      keyboard={false}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ enabled: true, recipients: [] }}
      >
        <Form.Item
          label={scopeType === "vessel" ? "Vessel" : "Shipco"}
          name="scopeId"
          rules={[{ required: true, message: "Select a scope" }]}
        >
          <Select
            {...scopeSelectProps}
            placeholder={scopeType === "vessel" ? "Select vessel" : "Select shipco"}
          />
        </Form.Item>
        <Form.Item label="Enabled" name="enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Recipients" name="recipients">
          <RecipientsInput value={[]} onChange={() => undefined} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const FlatFeeAlertSettingsPage: React.FC = () => {
  const apiUrl = useApiUrl();
  const [activeTab, setActiveTab] = React.useState<ScopeType>("shipco");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [reloadTokens, setReloadTokens] = React.useState<
    Record<ScopeType, number>
  >({ shipco: 0, vessel: 0 });

  // Current UTC year - fired-state rows are scoped by year server-side.
  const currentYear = new Date().getUTCFullYear();

  const { result: stateResult } = useCustom<AlertStateRow[]>({
    url: `${apiUrl}/flat-fee-alerts/state`,
    method: "get",
    config: { query: { year: currentYear } },
  });

  const stateData = stateResult?.data;

  const firedByScope = React.useMemo(() => {
    // useCustom's pre-load value is a frozen EMPTY_OBJECT ({}), which is
    // truthy, so `stateData ?? []` never falls back for this bare-array
    // endpoint. Guard explicitly (same trap as ShipcoVessels in
    // analytics.tsx) or the loop below throws on first render.
    const states = Array.isArray(stateData) ? stateData : [];
    const map = new Map<string, AlertStateRow[]>();
    for (const state of states) {
      const key = scopeKey(state.scopeType, state.scopeId);
      const list = map.get(key) ?? [];
      list.push(state);
      map.set(key, list);
    }
    return map;
  }, [stateData]);

  const bumpReload = (scopeType: ScopeType) =>
    setReloadTokens((prev) => ({ ...prev, [scopeType]: prev[scopeType] + 1 }));

  return (
    <>
      <Space style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <Button type="primary" onClick={() => setModalOpen(true)}>
          New alert
        </Button>
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as ScopeType)}
        items={[
          {
            key: "shipco",
            label: "Shipcos",
            children: (
              <AlertConfigPanel
                scopeType="shipco"
                firedByScope={firedByScope}
                reloadToken={reloadTokens.shipco}
              />
            ),
          },
          {
            key: "vessel",
            label: "Vessels",
            children: (
              <AlertConfigPanel
                scopeType="vessel"
                firedByScope={firedByScope}
                reloadToken={reloadTokens.vessel}
              />
            ),
          },
        ]}
      />

      <NewAlertModal
        open={modalOpen}
        scopeType={activeTab}
        onClose={() => setModalOpen(false)}
        onCreated={bumpReload}
      />
    </>
  );
};
